import { STORAGE } from "../storage";
import { Store } from "../../reducers/store";
import { setToken } from "../../reducers/configSlice";
import { dispatchAction } from "../state/dispatchAction";
import { apiRegisterDevice } from "../../api/actions";
import { getDeviceUuid } from "../helpers/getDeviceUuid";

/**
 * Token Refresh Service
 * 
 * Handles automatic token refresh when API requests return 401 Unauthorized.
 * Uses the stored device UUID and refresh_token parameter to get a new token.
 */

let isRefreshing = false;
let refreshPromise = null;

/**
 * Refresh the authentication token
 * @returns {Promise<string|null>} New token or null if refresh failed
 */
export const refreshAuthToken = async () => {
    // Prevent multiple simultaneous refresh attempts
    if (isRefreshing) {
        return refreshPromise;
    }

    isRefreshing = true;
    
    try {
        console.log('REFRESH_TOKEN_SERVICE - Starting token refresh...');

        // Get device UUID from multiple sources (storage, token, etc.)
        const deviceUuid = await getDeviceUuid();
        if (!deviceUuid) {
            console.error('REFRESH_TOKEN_SERVICE - No device UUID found in any source (storage, token, config)');
            return null;
        }

        // Get current server URL and company ID from Redux state
        const state = Store.getState();
        const serverUrl = state.data.serverUrl;
        
        if (!serverUrl) {
            console.error('REFRESH_TOKEN_SERVICE - No server URL found in state');
            return null;
        }

        // Extract company ID from device config if available
        let companyId = state.data.config?.company_id;

        if (!companyId) {
            // Try to get from stored config or device ID
            const deviceId = state.data.config?.device_id;
            if (deviceId) {
                // Extract company ID from device ID if it follows a pattern
                // This is a fallback - adjust based on your device ID format
                companyId = 1; // Default fallback
            } else {
                console.warn('REFRESH_TOKEN_SERVICE - No company ID or device ID found, using fallback');
                companyId = 1; // Fallback company ID
            }
        }

        console.log('REFRESH_TOKEN_SERVICE - Attempting refresh with UUID:', deviceUuid);

        // Call apiRegisterDevice with refresh_token parameter
        refreshPromise = apiRegisterDevice(deviceUuid, null, companyId, true, { refresh_token: 1 });
        const { response, error } = await refreshPromise;

        if (error || !response?.token) {
            console.error('REFRESH_TOKEN_SERVICE - Token refresh failed:', error);
            return null;
        }

        const newToken = response.token;
        console.log('REFRESH_TOKEN_SERVICE - Token refresh successful');

        // Update storage and Redux state
        await STORAGE.set("token", newToken);
        dispatchAction(setToken(newToken));

        return newToken;

    } catch (error) {
        console.error('REFRESH_TOKEN_SERVICE - Token refresh error:', error);
        return null;
    } finally {
        isRefreshing = false;
        refreshPromise = null;
    }
};

/**
 * Check if a response indicates an unauthorized error
 * @param {Object} response - API response object
 * @returns {boolean} True if response indicates unauthorized
 */
export const isUnauthorizedResponse = (response) => {
    // Check for 401 status code
    if (response?.error?.status === 401) {
        return true;
    }

    // Check for specific error response format
    if (response?.error?.data?.success === 0 && 
        response?.error?.data?.error === "Unauthorized") {
        return true;
    }

    // Check for direct error format
    if (response?.data?.success === 0 && 
        response?.data?.error === "Unauthorized") {
        return true;
    }

    return false;
};

/**
 * Handle API response and refresh token if needed
 * @param {Function} apiCall - Function that makes the API call
 * @param {number} maxRetries - Maximum number of retry attempts (default: 1)
 * @returns {Promise<Object>} API response
 */
export const handleApiResponseWithRefresh = async (apiCall, maxRetries = 1) => {
    let retryCount = 0;
    
    while (retryCount <= maxRetries) {
        const response = await apiCall();
        
        // If not unauthorized, return the response
        if (!isUnauthorizedResponse(response)) {
            return response;
        }
        
        // If this is our last retry, return the error
        if (retryCount >= maxRetries) {
            console.error('REFRESH_TOKEN_SERVICE - Max retries reached, returning error');
            return response;
        }
        
        console.log(`REFRESH_TOKEN_SERVICE - Unauthorized response, attempting refresh (retry ${retryCount + 1}/${maxRetries + 1})`);
        
        // Attempt to refresh token
        const newToken = await refreshAuthToken();
        
        if (!newToken) {
            console.error('REFRESH_TOKEN_SERVICE - Token refresh failed, returning error');
            return response;
        }
        
        console.log('REFRESH_TOKEN_SERVICE - Token refreshed, retrying original request');
        retryCount++;
    }
};

export default {
    refreshAuthToken,
    isUnauthorizedResponse,
    handleApiResponseWithRefresh
};
